import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import {
  financeApi,
  type CoaAccount,
  type AccountLedgerStatement,
} from '@/api/financeApi';
import { formatINR } from '@/utils/format';

export default function AccountLedgerPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const accountIdParam = params.get('accountId');

  const today = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1;

  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [selectedId, setSelectedId] = useState(accountIdParam || '');
  const [fromDate, setFromDate] = useState(`${currentYear}-04-01`);
  const [toDate, setToDate] = useState(today);

  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState<AccountLedgerStatement | null>(null);

  // Load active accounts
  useEffect(() => {
    async function loadAccounts() {
      try {
        const accs = await financeApi.getAccounts({ isActive: true });
        setAccounts(accs);
        if (!selectedId && accs.length > 0) {
          setSelectedId(accs[0].id);
        }
      } catch (err) {
        console.error('Failed to load accounts for ledger:', err);
      }
    }
    loadAccounts();
  }, [selectedId]);

  // Sync param
  useEffect(() => {
    if (accountIdParam) {
      setSelectedId(accountIdParam);
    }
  }, [accountIdParam]);

  // Load statement
  const loadStatement = useCallback(async () => {
    if (!selectedId) return;
    try {
      setLoading(true);
      const data = await financeApi.getAccountLedger(selectedId, { fromDate, toDate, limit: 100 });
      setStatement(data);
    } catch (err: any) {
      console.error('Failed to load ledger statement:', err);
      toast.error(err.response?.data?.message || 'Failed to load ledger statement');
    } finally {
      setLoading(false);
    }
  }, [selectedId, fromDate, toDate, toast]);

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  const handleExport = () => {
    if (!statement) return;
    const rows: (string | number)[][] = [
      ['Opening Balance', '', '', '', '', statement.openingBalance],
    ];
    (statement.entries || []).forEach((e) => {
      rows.push([
        e.entryDate,
        e.voucherNumber,
        e.partyName || e.narration,
        e.debit > 0 ? e.debit : 0,
        e.credit > 0 ? e.credit : 0,
        e.runningBalance,
      ]);
    });
    rows.push(['Closing Balance', '', '', '', '', statement.closingBalance]);

    const csv = [
      ['Date', 'Voucher Number', 'Particulars / Narration', 'Debit (₹)', 'Credit (₹)', 'Running Balance (₹)'].join(','),
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Ledger_${statement.account.code}_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Account statement exported');
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    const dt = new Date(d.includes('T') ? d : d + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const selectedAccount = accounts.find((a) => a.id === selectedId) || statement?.account;

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 print:hidden">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/finance/accounts')}
                className="w-8 h-8 rounded-lg border border-[#e2e8f0] flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#1e293b]">Account Ledger Statement</h1>
                <p className="text-xs text-slate-500">Chronological transactional statement with running balance</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-printer-line text-sm text-slate-500" /> Print
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-download-2-line text-sm text-primary-600" /> Export CSV
              </button>
              <button
                onClick={() => navigate(`/finance/journal/new?accountId=${selectedId}`)}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-add-line text-sm" /> New Entry
              </button>
            </div>
          </div>

          {/* Account Selector & Date Range Filter */}
          <div className="px-6 pt-5 flex items-center justify-between gap-4 flex-wrap print:hidden">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-80">
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="w-full h-9 px-3 text-xs bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] font-semibold text-[#1e293b] shadow-sm cursor-pointer"
                >
                  <option value="">-- Choose Account --</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.code} — {acc.name} ({acc.type})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-white border border-[#e2e8f0] rounded-lg px-3 h-9 shadow-sm">
                <span className="text-slate-400">From:</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="text-sm text-[#1e293b] focus:outline-none bg-transparent"
                />
                <span className="text-slate-400">To:</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="text-sm text-[#1e293b] focus:outline-none bg-transparent"
                />
              </div>
            </div>

            {selectedAccount && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-50 text-primary-800 border border-primary-200">
                Normal Balance: {selectedAccount.balanceType}
              </span>
            )}
          </div>

          {/* Account Overview Cards */}
          {statement && (
            <div className="px-6 pt-5 grid grid-cols-1 sm:grid-cols-4 gap-3.5">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Opening Balance</p>
                <p className="text-lg font-bold font-mono text-[#1e293b] mt-1">
                  {formatINR(statement.openingBalance)}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">As of {formatDate(fromDate)}</p>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Debits</p>
                <p className="text-lg font-bold font-mono text-blue-700 mt-1">
                  {formatINR(statement.totalDebit)}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Money In / Expense</p>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Credits</p>
                <p className="text-lg font-bold font-mono text-purple-700 mt-1">
                  {formatINR(statement.totalCredit)}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Money Out / Revenue</p>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Closing Balance</p>
                <p className="text-lg font-bold font-mono text-emerald-700 mt-1">
                  {formatINR(statement.closingBalance)}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">As of {formatDate(toDate)}</p>
              </div>
            </div>
          )}

          {/* Statement Table */}
          <div className="px-6 pt-5">
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              {loading ? (
                <div className="py-20 text-center text-slate-400 flex flex-col items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
                  <p className="text-xs">Loading ledger entries...</p>
                </div>
              ) : !statement ? (
                <div className="py-16 text-center text-slate-400">
                  <p className="text-sm">Select an account to view its ledger transactions.</p>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="text-left px-5 py-3 w-28">Date</th>
                      <th className="text-left px-4 py-3 w-36">Voucher #</th>
                      <th className="text-left px-4 py-3 w-28">Type</th>
                      <th className="text-left px-4 py-3">Particulars / Party / Narration</th>
                      <th className="text-right px-4 py-3 w-32">Debit (Dr ₹)</th>
                      <th className="text-right px-4 py-3 w-32">Credit (Cr ₹)</th>
                      <th className="text-right px-5 py-3 w-36 bg-slate-100/40">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {/* Opening Balance Row */}
                    <tr className="bg-slate-50/60 font-semibold text-slate-800">
                      <td className="px-5 py-2.5 text-slate-500 font-mono">{formatDate(fromDate)}</td>
                      <td className="px-4 py-2.5 font-mono text-slate-400">OP-BAL</td>
                      <td className="px-4 py-2.5 text-slate-400">—</td>
                      <td className="px-4 py-2.5 italic text-slate-600">Opening Balance Brought Forward</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-400">—</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-400">—</td>
                      <td className="px-5 py-2.5 text-right font-mono font-bold text-[#1e293b] bg-slate-50">
                        {formatINR(statement.openingBalance)}
                      </td>
                    </tr>

                    {/* Transaction Rows */}
                    {(statement.entries || []).map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => navigate(`/finance/journal/${row.voucherId}`)}
                        className="hover:bg-primary-50/30 cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-2.5 text-slate-700 font-mono">{formatDate(row.entryDate)}</td>
                        <td className="px-4 py-2.5 font-mono font-bold text-primary-700 hover:underline">
                          {row.voucherNumber}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200/70 text-slate-700">
                            {row.voucherType}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          {row.partyName && (
                            <span className="font-semibold text-blue-700 mr-2">[{row.partyName}]</span>
                          )}
                          <span className="text-slate-800">{row.narration || '—'}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-blue-800">
                          {row.debit > 0 ? formatINR(row.debit) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-purple-800">
                          {row.credit > 0 ? formatINR(row.credit) : '—'}
                        </td>
                        <td className="px-5 py-2.5 text-right font-mono font-bold text-[#1e293b] bg-slate-50/40">
                          {formatINR(row.runningBalance)}
                        </td>
                      </tr>
                    ))}

                    {statement.entries?.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-400">
                          No transactions recorded in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-100/90 font-bold border-t-2 border-slate-300">
                    <tr>
                      <td colSpan={4} className="px-5 py-3 text-right uppercase tracking-wider text-xs text-slate-800">
                        Period Totals & Closing Balance
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-bold text-blue-900">
                        {formatINR(statement.totalDebit)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-bold text-purple-900">
                        {formatINR(statement.totalCredit)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-sm font-bold text-emerald-950 bg-emerald-100/50">
                        {formatINR(statement.closingBalance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}